import { forwardRef, useState } from 'react';
import NotificationInfo from './NotificationInfo';
import { useTranslation } from '../../hooks/useTranslation';
import { NotificationFormatter } from '../../util/NotificationFormatter/NotificationFormatter';
import { useHttpGetNotificationsByUserOnMount, useHttpPatchMarkAllNotificationsAsRead, useHttpPatchMarkNotificationAsRead } from '../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserSub } from '../../store/slices/auth.slice';
import { NoDataImage } from '../Dashboard/ProjectOverview/NoDataImage';
import { markAllNotificationsAsRead, markNotificationAsRead } from '../../store/slices/notification-center.slice';

interface NotificationsProps {}

const Notifications = forwardRef<HTMLDivElement, NotificationsProps>((_props, ref) => {
    const { t, data } = useTranslation();
    const authUserSub = useSelector(selectUserSub);
    const { data: notifications } = useHttpGetNotificationsByUserOnMount();
    const formatter = new NotificationFormatter(authUserSub, data);
    const [isExpanded, setIsExpanded] = useState(false);
    const { execPatch: markAsRead } = useHttpPatchMarkNotificationAsRead();
    const { execPatch: markAllAsRead } = useHttpPatchMarkAllNotificationsAsRead();
    const dispatch = useDispatch();

    const [readStatus, setReadStatus] = useState<{ [key: string]: boolean }>({});

    const formattedNotifications = [...notifications.items]
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((notification: any) => {
            const leaveRequestId = notification.message?.leaveRequestId || undefined;
            const fromDate = notification.message?.fromDate ? new Date(notification.message.fromDate) : undefined;
            const medicalLeaveRequestId = notification.message?.medicalLeaveRequestId || undefined;

            return {
                id: notification.id,
                type: notification.type,
                title: formatter.generateTitle(notification.title),
                message: formatter.generateMessage(notification),
                recipients: notification.recipients,
                initiators: notification.initiators,
                userInteractionStatus: notification.userInteractionStatus,
                originalTitle: notification.title,
                leaveRequestId,
                medicalLeaveRequestId,
                fromDate,
            };
        });

    const handleMarkAsRead = (id: number) => {
        markAsRead(id.toString(), () => {
            setReadStatus(prev => ({ ...prev, [id]: true }));
            dispatch(markNotificationAsRead(id.toString()));
        });
    };

    const handleMarkAllAsRead = () => {
        markAllAsRead(() => {
            const updatedStatus = formattedNotifications.reduce((acc, notification) => {
                acc[notification.id] = true;
                return acc;
            }, {} as { [key: string]: boolean });

            setReadStatus(updatedStatus);
            dispatch(markAllNotificationsAsRead());
        });
    };

    const visibleNotifications = isExpanded ? formattedNotifications : formattedNotifications.slice(0, 3);
    const toggleNotifications = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div ref={ref} className="notifications__wrapper">
            <div className="notifications__title">
                <p className="notifications__header">{t('notifications.notifications')}</p>
                {formattedNotifications.length > 0 && (
                    <p className="notifications__paragraph" onClick={handleMarkAllAsRead}>
                        {t('notifications.markAllAsRead')}
                    </p>
                )}
            </div>
            {formattedNotifications.length > 0 ? (
                <>
                    <ul className="notifications__list">
                        {visibleNotifications.map((notification: any) => (
                            <NotificationInfo
                                id={notification.id.toString()}
                                type={formatter.getIcon(notification.originalTitle)}
                                key={notification.id}
                                title={notification.title}
                                message={notification.message}
                                isRead={readStatus[notification.id] || notification.userInteractionStatus === 'READ'}
                                onClick={() => handleMarkAsRead(notification.id)}
                                isFormattedMessage={
                                    notification.originalTitle === 'LEAVE_REQUEST_APPROVE' ||
                                    notification.originalTitle === 'LEAVE_REQUEST_REJECT' ||
                                    notification.originalTitle === 'LEAVE_REQUEST_DELETE' ||
                                    notification.originalTitle === 'LEAVE_REQUEST_UPDATE_ONGOING' ||
                                    notification.originalTitle === 'LEAVE_REQUEST_CREATION' ||
                                    notification.originalTitle === 'MEDICAL_LEAVE_REQUEST_CREATION'
                                }
                                isAction={notification.type === 'ACTION_REQUIRED'}
                                leaveRequestId={notification.leaveRequestId}
                                medicalLeaveRequestId={notification.medicalLeaveRequestId}
                                originalTitle={notification.originalTitle}
                                fromDate={notification.fromDate}
                            />
                        ))}
                    </ul>
                    {formattedNotifications.length > 3 && (
                        <div className="notifications__show-more" onClick={toggleNotifications}>
                            <p className="paragraph-4 notifications__paragraph">
                                {t(isExpanded ? 'notifications.showLess' : 'notifications.showMore')}
                            </p>
                        </div>
                    )}
                </>
            ) : (
                <NoDataImage description={t('notifications.noNotifications')} />
            )}
        </div>
    );
});

export default Notifications;
